import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';
import { cachedConnectionCheck, clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export const payuApi = {
  // Per-business connection management
  async connect(payload: {
    businessId: string;
    merchantKey: string;
    salt: string;
    mode?: 'test' | 'prod';
    accountName?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/payu/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to connect PayU');
    }
    clearConnectionCheckCache(`payu:${payload.businessId}`);
    return json;
  },

  async disconnect(businessId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/payu/${businessId}/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to disconnect PayU');
    }
    clearConnectionCheckCache(`payu:${businessId}`);
    return json;
  },

  // Cached + in-flight-deduped for ~30s; cleared on connect/disconnect.
  async checkConnection(businessId: string) {
    return cachedConnectionCheck(`payu:${businessId}`, async () => {
      const res = await fetch(`${API_CONFIG.BASE_URL}/payu/${businessId}/connection`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to check PayU connection');
      }
      return json;
    });
  },

  async testConnection(payload: {
    merchantKey: string;
    salt: string;
    mode?: 'test' | 'prod';
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/payu/test-connection`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to validate PayU credentials');
    }
    return json;
  },

  async createPaymentLink(
    businessId: string,
    payload: {
      amount: number;
      productinfo: string;
      firstname?: string;
      email?: string;
      phone?: string;
      returnUrl?: string;
    }
  ) {
    const res = await fetch(
      `${API_CONFIG.BASE_URL}/payu/${businessId}/payment-links`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      }
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create PayU payment link');
    }
    return json;
  },

  async listPaymentLinks(
    businessId: string,
    params?: { page?: number; limit?: number }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    const url = `${API_CONFIG.BASE_URL}/payu/${businessId}/payment-links${
      query.toString() ? `?${query.toString()}` : ''
    }`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list PayU payment links');
    }
    return json;
  },

  async listRefunds(
    businessId: string,
    params?: { page?: number; limit?: number }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    const url = `${API_CONFIG.BASE_URL}/payu/${businessId}/refunds${
      query.toString() ? `?${query.toString()}` : ''
    }`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list PayU refunds');
    }
    return json;
  },

  async listPayments(
    businessId: string,
    params?: { page?: number; limit?: number; status?: string }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.status) query.append('status', params.status);
    const url = `${API_CONFIG.BASE_URL}/payu/${businessId}/payments${
      query.toString() ? `?${query.toString()}` : ''
    }`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list PayU payments');
    }
    return json;
  },

  async refundPayment(
    businessId: string,
    payload: {
      paymentId: string;
      amount?: number;
      refundRefId?: string;
      command?: 'refund_transaction' | 'cancel_refund_transaction';
    }
  ) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/payu/${businessId}/refunds`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to issue PayU refund');
    }
    return json;
  },

  async createOrder(payload: {
    amount: number;
    productinfo?: string;
    firstname?: string;
    email?: string;
    phone?: string;
    returnUrl?: string;
    failureUrl?: string;
    receipt?: string;
    udf1?: string;
    udf2?: string;
    udf3?: string;
    udf4?: string;
    udf5?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/payu/create-order`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create PayU order');
    }
    return json;
  },
};
